import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { 
  Search, 
  RefreshCw, 
  MapPin, 
  Calendar,
  MessageSquare,
  Save,
  X,
  Bike,
  Car,
  Utensils,
  Users,
  Clock,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Filter,
  Download,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FilterX,
  Video,
  Instagram,
  Store,
  Rocket
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
} from "@/components/ui/card";

export const CityManagementCRM = () => {
  const { user } = useAuth();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  
  // Filters - default to empty/none
  const [promotionFilter, setPromotionFilter] = useState("");
  const [influencerFilter, setInfluencerFilter] = useState("");
  const [restaurantFilter, setRestaurantFilter] = useState("");
  const [followUpFrom, setFollowUpFrom] = useState("");
  const [followUpTo, setFollowUpTo] = useState("");
  
  // Sorting
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  
  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Edit dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  const [editForm, setEditForm] = useState({
    promotion_status: "",
    influencer_status: "",
    restaurant_status: "",
    launch_status: "",
    messages_status: "",
    follow_up_date: "",
    launch_date: "",
    admin_comments: "",
    freelance_food_delivery_enabled: true,
    max_parallel_food_orders: ""
  });
  const [saving, setSaving] = useState(false);
  
  // Filters panel visibility
  const [showFilters, setShowFilters] = useState(false);
  
  // Bulk radius update
  const [showRadiusDialog, setShowRadiusDialog] = useState(false);
  const [bulkRadius, setBulkRadius] = useState("20");
  const [updatingRadius, setUpdatingRadius] = useState(false);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch cities
  const fetchCities = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      
      if (debouncedSearch) params.append("search", debouncedSearch);
      if (promotionFilter) params.append("promotion_status", promotionFilter);
      if (influencerFilter) params.append("influencer_status", influencerFilter);
      if (restaurantFilter) params.append("restaurant_status", restaurantFilter);
      if (followUpFrom) params.append("follow_up_from", followUpFrom);
      if (followUpTo) params.append("follow_up_to", followUpTo);
      
      const response = await axios.get(
        `${API}/admin/${user.id}/cities/management?${params.toString()}`
      );
      
      setCities(response.data.cities || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 0);
    } catch (error) {
      console.error("Error fetching cities:", error);
      toast.error("Failed to fetch cities");
    } finally {
      setLoading(false);
    }
  }, [user?.id, page, limit, debouncedSearch, promotionFilter, influencerFilter, restaurantFilter, followUpFrom, followUpTo, sortBy, sortOrder]);

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  // Clear all filters
  const clearFilters = () => {
    setPromotionFilter("");
    setInfluencerFilter("");
    setRestaurantFilter("");
    setFollowUpFrom("");
    setFollowUpTo("");
    setSearchInput("");
    setPage(1);
  };

  // Check if any filter is active
  const hasActiveFilters = promotionFilter || influencerFilter || restaurantFilter || 
    followUpFrom || followUpTo || searchInput;

  // Handle column sort
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
    setPage(1);
  };

  // Get sort icon
  const getSortIcon = (column) => {
    if (sortBy !== column) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />;
    return sortOrder === "asc" 
      ? <ArrowUp className="w-3 h-3 ml-1 text-primary" /> 
      : <ArrowDown className="w-3 h-3 ml-1 text-primary" />;
  };

  // Open edit dialog
  const openEditDialog = (city) => {
    setSelectedCity(city);
    setEditForm({
      promotion_status: city.promotion_status || "",
      influencer_status: city.influencer_status || "",
      restaurant_status: city.restaurant_status || "",
      launch_status: city.launch_status || "",
      messages_status: city.messages_status || "",
      follow_up_date: city.follow_up_date ? city.follow_up_date.split('T')[0] : "",
      launch_date: city.launch_date ? city.launch_date.split('T')[0] : "",
      admin_comments: city.admin_comments || "",
      freelance_food_delivery_enabled: city.freelance_food_delivery_enabled !== false,  // Default true
      max_parallel_food_orders: city.max_parallel_food_orders?.toString() || ""
    });
    setShowEditDialog(true);
  };

  // Save city details
  const saveCityDetails = async () => {
    if (!selectedCity) return;
    setSaving(true);
    try {
      // Prepare form data, converting max_parallel_food_orders to number or null
      const formData = {
        ...editForm,
        max_parallel_food_orders: editForm.max_parallel_food_orders === "" ? null : parseInt(editForm.max_parallel_food_orders)
      };
      await axios.put(
        `${API}/admin/${user.id}/cities/${selectedCity.id}/management`,
        formData
      );
      toast.success("City details updated successfully");
      setShowEditDialog(false);
      fetchCities();
    } catch (error) {
      console.error("Error updating city:", error);
      toast.error(error.response?.data?.detail || "Failed to update city");
    } finally {
      setSaving(false);
    }
  };

  // Get promotion status badge
  const getPromotionBadge = (status) => {
    switch (status) {
      case "not_started":
        return <Badge className="bg-gray-100 text-gray-600">Not Started</Badge>;
      case "reel_1_in_progress":
        return <Badge className="bg-yellow-100 text-yellow-700">Reel 1 WIP</Badge>;
      case "reel_1_posted":
        return <Badge className="bg-blue-100 text-blue-700">Reel 1 Posted</Badge>;
      case "reel_2_in_progress":
        return <Badge className="bg-orange-100 text-orange-700">Reel 2 WIP</Badge>;
      case "reel_2_posted":
        return <Badge className="bg-green-100 text-green-700">Reel 2 Posted</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-400">-</Badge>;
    }
  };

  // Get influencer status badge
  const getInfluencerBadge = (status) => {
    switch (status) {
      case "not_contacted":
        return <Badge className="bg-gray-100 text-gray-600">Not Contacted</Badge>;
      case "contacted":
        return <Badge className="bg-blue-100 text-blue-700">Contacted</Badge>;
      case "wip":
        return <Badge className="bg-yellow-100 text-yellow-700">WIP</Badge>;
      case "posted":
        return <Badge className="bg-green-100 text-green-700">Posted</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-400">-</Badge>;
    }
  };

  // Get restaurant status badge
  const getRestaurantBadge = (status) => {
    switch (status) {
      case "listed":
        return <Badge className="bg-blue-100 text-blue-700">Listed</Badge>;
      case "onboarding":
        return <Badge className="bg-yellow-100 text-yellow-700">Onboarding</Badge>;
      case "complete":
        return <Badge className="bg-green-100 text-green-700">Complete</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-400">-</Badge>;
    }
  };

  // Get launch status badge
  const getLaunchStatusBadge = (status) => {
    switch (status) {
      case "not_ready":
        return <Badge className="bg-red-100 text-red-700">Not Ready</Badge>;
      case "ready_to_launch":
        return <Badge className="bg-yellow-100 text-yellow-700">Ready</Badge>;
      case "launched":
        return <Badge className="bg-green-100 text-green-700">Launched</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-400">-</Badge>;
    }
  };

  // Get messages status badge
  const getMessagesBadge = (status) => {
    switch (status) {
      case "not_sent":
        return <Badge className="bg-gray-100 text-gray-600">Not Sent</Badge>;
      case "sent":
        return <Badge className="bg-green-100 text-green-700">Sent</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-400">-</Badge>;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Export to CSV - fetches ALL data, not just current page
  const exportToCSV = async () => {
    try {
      toast.info("Fetching all data for export...");
      
      // Fetch ALL cities without pagination
      const params = new URLSearchParams({
        page: "1",
        limit: "10000",  // Large limit to get all
      });
      
      // Apply current filters
      if (debouncedSearch) params.append("search", debouncedSearch);
      if (promotionFilter) params.append("promotion_status", promotionFilter);
      if (influencerFilter) params.append("influencer_status", influencerFilter);
      if (restaurantFilter) params.append("restaurant_status", restaurantFilter);
      if (followUpFrom) params.append("follow_up_from", followUpFrom);
      if (followUpTo) params.append("follow_up_to", followUpTo);
      
      const response = await axios.get(
        `${API}/admin/${user.id}/cities/management?${params.toString()}`
      );
      
      const allCities = response.data.cities || [];
      
      const headers = ["City", "State", "Bike Riders", "Auto Riders", "Food Delivery Riders", "Freelance", "Customers", "Promotion Status", "Influencer Status", "Restaurant Status", "Launch Status", "Messages", "Follow Up Date", "Launch Date", "Comments"];
      const rows = allCities.map(c => [
        c.name,
        c.state || "-",
        c.bike_riders,
        c.auto_riders,
        c.food_delivery_riders,
        c.freelance_food_delivery_enabled !== false ? "Open" : "Restricted",
        c.customer_count,
        c.promotion_status || "-",
        c.influencer_status || "-",
        c.restaurant_status || "-",
        c.launch_status || "-",
        c.messages_status || "-",
        c.follow_up_date ? formatDate(c.follow_up_date) : "-",
        c.launch_date ? formatDate(c.launch_date) : "-",
        (c.admin_comments || "").replace(/,/g, ";")
      ]);
      
      const csvContent = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cities_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      
      toast.success(`Exported ${allCities.length} cities`);
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error("Failed to export data");
    }
  };

  // Bulk update radius for all cities
  const handleBulkRadiusUpdate = async () => {
    const radiusNum = parseFloat(bulkRadius);
    if (isNaN(radiusNum) || radiusNum < 1 || radiusNum > 500) {
      toast.error("Radius must be between 1 and 500 km");
      return;
    }
    
    setUpdatingRadius(true);
    try {
      const response = await axios.patch(
        `${API}/admin/cities/bulk-update-radius?admin_id=${user.id}`,
        { radius_km: radiusNum }
      );
      toast.success(response.data.message);
      setShowRadiusDialog(false);
      fetchCities(); // Refresh to show updated radius
    } catch (error) {
      console.error("Error updating radius:", error);
      toast.error(error.response?.data?.detail || "Failed to update radius");
    } finally {
      setUpdatingRadius(false);
    }
  };

  // Calculate totals for stats
  const totalBikeRiders = cities.reduce((sum, c) => sum + (c.bike_riders || 0), 0);
  const totalAutoRiders = cities.reduce((sum, c) => sum + (c.auto_riders || 0), 0);
  const totalFoodRiders = cities.reduce((sum, c) => sum + (c.food_delivery_riders || 0), 0);
  const totalCustomers = cities.reduce((sum, c) => sum + (c.customer_count || 0), 0);

  return (
    <div className="space-y-6" data-testid="city-management-crm">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <MapPin className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">City Management</h2>
            <p className="text-sm text-gray-500">Track city launch status, promotions, and growth</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowRadiusDialog(true)} 
            data-testid="bulk-radius-btn"
          >
            <MapPin className="w-4 h-4 mr-2" />
            Bulk Radius
          </Button>
          <Button variant="outline" onClick={exportToCSV} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={fetchCities} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <MapPin className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{total}</p>
                <p className="text-xs text-gray-500">Total Cities</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Bike className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalBikeRiders}</p>
                <p className="text-xs text-gray-500">Bike Riders</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Car className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalAutoRiders}</p>
                <p className="text-xs text-gray-500">Auto Riders</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Utensils className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalFoodRiders}</p>
                <p className="text-xs text-gray-500">Food Delivery</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Users className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalCustomers}</p>
                <p className="text-xs text-gray-500">Customers</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter Toggle */}
      <div className="flex flex-wrap gap-4 items-center bg-gray-50 p-4 rounded-lg">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search by city name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </div>
        
        <Button 
          variant={showFilters ? "default" : "outline"} 
          onClick={() => setShowFilters(!showFilters)}
          data-testid="toggle-filters-btn"
        >
          <Filter className="w-4 h-4 mr-2" />
          Filters
          {hasActiveFilters && (
            <span className="ml-2 w-2 h-2 bg-red-500 rounded-full" />
          )}
        </Button>

        {hasActiveFilters && (
          <Button variant="ghost" onClick={clearFilters} data-testid="clear-filters-btn">
            <FilterX className="w-4 h-4 mr-2" />
            Clear All
          </Button>
        )}
      </div>

      {/* Expandable Filters Panel */}
      {showFilters && (
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {/* Promotion Status Filter */}
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">Promotion Status</Label>
              <Select value={promotionFilter || "all"} onValueChange={(v) => { setPromotionFilter(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger data-testid="promotion-filter">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Promotion</SelectItem>
                  <SelectItem value="not_started">Not Started</SelectItem>
                  <SelectItem value="reel_1_in_progress">Reel 1 In Progress</SelectItem>
                  <SelectItem value="reel_1_posted">Reel 1 Posted</SelectItem>
                  <SelectItem value="reel_2_in_progress">Reel 2 In Progress</SelectItem>
                  <SelectItem value="reel_2_posted">Reel 2 Posted</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Influencer Status Filter */}
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">Influencer Status</Label>
              <Select value={influencerFilter || "all"} onValueChange={(v) => { setInfluencerFilter(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger data-testid="influencer-filter">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Influencer</SelectItem>
                  <SelectItem value="not_contacted">Not Contacted</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="wip">WIP</SelectItem>
                  <SelectItem value="posted">Posted</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Restaurant Status Filter */}
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">Restaurant Status</Label>
              <Select value={restaurantFilter || "all"} onValueChange={(v) => { setRestaurantFilter(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger data-testid="restaurant-filter">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Restaurant</SelectItem>
                  <SelectItem value="listed">Listed</SelectItem>
                  <SelectItem value="onboarding">Onboarding</SelectItem>
                  <SelectItem value="complete">Complete</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Follow Up Date Filters */}
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">Follow Up From</Label>
              <Input
                type="date"
                value={followUpFrom}
                onChange={(e) => { setFollowUpFrom(e.target.value); setPage(1); }}
                data-testid="follow-up-from"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">Follow Up To</Label>
              <Input
                type="date"
                value={followUpTo}
                onChange={(e) => { setFollowUpTo(e.target.value); setPage(1); }}
                data-testid="follow-up-to"
              />
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead 
                className="font-semibold cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("name")}
              >
                <div className="flex items-center">
                  City
                  {getSortIcon("name")}
                </div>
              </TableHead>
              <TableHead 
                className="font-semibold cursor-pointer hover:bg-gray-100 text-center"
                onClick={() => handleSort("bike_riders")}
              >
                <div className="flex items-center justify-center">
                  <Bike className="w-4 h-4 mr-1" />
                  Bike
                  {getSortIcon("bike_riders")}
                </div>
              </TableHead>
              <TableHead 
                className="font-semibold cursor-pointer hover:bg-gray-100 text-center"
                onClick={() => handleSort("auto_riders")}
              >
                <div className="flex items-center justify-center">
                  <Car className="w-4 h-4 mr-1" />
                  Auto
                  {getSortIcon("auto_riders")}
                </div>
              </TableHead>
              <TableHead 
                className="font-semibold cursor-pointer hover:bg-gray-100 text-center"
                onClick={() => handleSort("food_delivery_riders")}
              >
                <div className="flex items-center justify-center">
                  <Utensils className="w-4 h-4 mr-1" />
                  Food
                  {getSortIcon("food_delivery_riders")}
                </div>
              </TableHead>
              <TableHead className="font-semibold text-center">
                <div className="flex items-center justify-center text-xs">
                  Freelance
                </div>
              </TableHead>
              <TableHead 
                className="font-semibold cursor-pointer hover:bg-gray-100 text-center"
                onClick={() => handleSort("customer_count")}
              >
                <div className="flex items-center justify-center">
                  <Users className="w-4 h-4 mr-1" />
                  Customers
                  {getSortIcon("customer_count")}
                </div>
              </TableHead>
              <TableHead 
                className="font-semibold cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("promotion_status")}
              >
                <div className="flex items-center">
                  <Video className="w-4 h-4 mr-1" />
                  Promotion
                  {getSortIcon("promotion_status")}
                </div>
              </TableHead>
              <TableHead 
                className="font-semibold cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("influencer_status")}
              >
                <div className="flex items-center">
                  <Instagram className="w-4 h-4 mr-1" />
                  Influencer
                  {getSortIcon("influencer_status")}
                </div>
              </TableHead>
              <TableHead 
                className="font-semibold cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("restaurant_status")}
              >
                <div className="flex items-center">
                  <Store className="w-4 h-4 mr-1" />
                  Restaurant
                  {getSortIcon("restaurant_status")}
                </div>
              </TableHead>
              <TableHead 
                className="font-semibold cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("launch_status")}
              >
                <div className="flex items-center">
                  <Rocket className="w-4 h-4 mr-1" />
                  Status
                  {getSortIcon("launch_status")}
                </div>
              </TableHead>
              <TableHead className="font-semibold">
                <div className="flex items-center text-xs">
                  Msgs
                </div>
              </TableHead>
              <TableHead 
                className="font-semibold cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("follow_up_date")}
              >
                <div className="flex items-center">
                  Follow Up
                  {getSortIcon("follow_up_date")}
                </div>
              </TableHead>
              <TableHead 
                className="font-semibold cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("launch_date")}
              >
                <div className="flex items-center">
                  <Rocket className="w-4 h-4 mr-1" />
                  Launch
                  {getSortIcon("launch_date")}
                </div>
              </TableHead>
              <TableHead className="font-semibold text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={14} className="text-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                  <p className="text-gray-500 mt-2">Loading cities...</p>
                </TableCell>
              </TableRow>
            ) : cities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={14} className="text-center py-8">
                  <MapPin className="w-12 h-12 mx-auto text-gray-300" />
                  <p className="text-gray-500 mt-2">No cities found</p>
                </TableCell>
              </TableRow>
            ) : (
              cities.map((city) => (
                <TableRow 
                  key={city.id} 
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => openEditDialog(city)}
                  data-testid={`city-row-${city.id}`}
                >
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900">{city.name}</span>
                      {city.state && (
                        <span className="text-xs text-gray-500">{city.state}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="font-medium text-blue-600">{city.bike_riders}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="font-medium text-green-600">{city.auto_riders}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="font-medium text-orange-600">{city.food_delivery_riders}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    {city.freelance_food_delivery_enabled !== false ? (
                      <Badge className="bg-green-100 text-green-700 text-xs">Open</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700 text-xs">Restricted</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="font-medium text-purple-600">{city.customer_count}</span>
                  </TableCell>
                  <TableCell>{getPromotionBadge(city.promotion_status)}</TableCell>
                  <TableCell>{getInfluencerBadge(city.influencer_status)}</TableCell>
                  <TableCell>{getRestaurantBadge(city.restaurant_status)}</TableCell>
                  <TableCell>{getLaunchStatusBadge(city.launch_status)}</TableCell>
                  <TableCell>{getMessagesBadge(city.messages_status)}</TableCell>
                  <TableCell>
                    <span className={
                      city.follow_up_date && new Date(city.follow_up_date) <= new Date()
                        ? "text-red-600 font-medium"
                        : city.follow_up_date
                        ? "text-gray-900"
                        : "text-gray-400"
                    }>
                      {formatDate(city.follow_up_date)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={city.launch_date ? "text-gray-900" : "text-gray-400"}>
                      {formatDate(city.launch_date)}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); openEditDialog(city); }}
                      data-testid={`edit-city-${city.id}`}
                    >
                      <MessageSquare className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} cities
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(1)}
              disabled={page === 1}
              data-testid="page-first"
            >
              <ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              data-testid="page-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="px-3 py-1 bg-gray-100 rounded text-sm">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              data-testid="page-next"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              data-testid="page-last"
            >
              <ChevronsRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              Edit City Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedCity && (
            <div className="space-y-4">
              {/* City Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{selectedCity.name}</p>
                    {selectedCity.state && (
                      <p className="text-sm text-gray-500">{selectedCity.state}</p>
                    )}
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-4 gap-2">
                  <div className="text-center">
                    <p className="text-lg font-bold text-blue-600">{selectedCity.bike_riders}</p>
                    <p className="text-xs text-gray-500">Bike</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-green-600">{selectedCity.auto_riders}</p>
                    <p className="text-xs text-gray-500">Auto</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-orange-600">{selectedCity.food_delivery_riders}</p>
                    <p className="text-xs text-gray-500">Food</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-purple-600">{selectedCity.customer_count}</p>
                    <p className="text-xs text-gray-500">Customers</p>
                  </div>
                </div>
              </div>

              {/* Freelance Food Delivery Toggle */}
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Freelance Food Delivery</Label>
                    <p className="text-sm text-gray-500 mt-1">
                      {editForm.freelance_food_delivery_enabled 
                        ? "Any bike rider can enable food delivery for themselves" 
                        : "Only admin-approved riders can do food delivery"}
                    </p>
                  </div>
                  <Switch
                    checked={editForm.freelance_food_delivery_enabled}
                    onCheckedChange={(checked) => setEditForm({...editForm, freelance_food_delivery_enabled: checked})}
                    data-testid="freelance-food-toggle"
                  />
                </div>
                {!editForm.freelance_food_delivery_enabled && (
                  <p className="text-xs text-orange-600 mt-2">
                    Riders must be manually approved in Rider Management to enable food delivery.
                  </p>
                )}
              </div>

              {/* Max Parallel Food Orders */}
              <div className="space-y-2">
                <Label>Max Parallel Food Orders (City Default)</Label>
                <Select 
                  value={editForm.max_parallel_food_orders || "default"} 
                  onValueChange={(v) => setEditForm({...editForm, max_parallel_food_orders: v === "default" ? "" : v})}
                >
                  <SelectTrigger data-testid="edit-max-parallel-orders">
                    <SelectValue placeholder="Default (1)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default (1 order at a time)</SelectItem>
                    <SelectItem value="1">1 - Single order</SelectItem>
                    <SelectItem value="2">2 - Two orders</SelectItem>
                    <SelectItem value="3">3 - Three orders</SelectItem>
                    <SelectItem value="4">4 - Four orders</SelectItem>
                    <SelectItem value="5">5 - Five orders</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Riders in this city can accept this many food orders simultaneously. Individual rider settings override this.
                </p>
              </div>

              {/* Promotion Status */}
              <div className="space-y-2">
                <Label>Promotion Status</Label>
                <Select 
                  value={editForm.promotion_status || "none"} 
                  onValueChange={(v) => setEditForm({...editForm, promotion_status: v === "none" ? "" : v})}
                >
                  <SelectTrigger data-testid="edit-promotion-status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not Set</SelectItem>
                    <SelectItem value="not_started">Not Started</SelectItem>
                    <SelectItem value="reel_1_in_progress">Reel 1 In Progress</SelectItem>
                    <SelectItem value="reel_1_posted">Reel 1 Posted</SelectItem>
                    <SelectItem value="reel_2_in_progress">Reel 2 In Progress</SelectItem>
                    <SelectItem value="reel_2_posted">Reel 2 Posted</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Influencer Status */}
              <div className="space-y-2">
                <Label>Influencer Status</Label>
                <Select 
                  value={editForm.influencer_status || "none"} 
                  onValueChange={(v) => setEditForm({...editForm, influencer_status: v === "none" ? "" : v})}
                >
                  <SelectTrigger data-testid="edit-influencer-status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not Set</SelectItem>
                    <SelectItem value="not_contacted">Not Contacted</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="wip">WIP</SelectItem>
                    <SelectItem value="posted">Posted</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Restaurant Status */}
              <div className="space-y-2">
                <Label>Restaurant Status</Label>
                <Select 
                  value={editForm.restaurant_status || "none"} 
                  onValueChange={(v) => setEditForm({...editForm, restaurant_status: v === "none" ? "" : v})}
                >
                  <SelectTrigger data-testid="edit-restaurant-status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not Set</SelectItem>
                    <SelectItem value="listed">Listed</SelectItem>
                    <SelectItem value="onboarding">Onboarding</SelectItem>
                    <SelectItem value="complete">Complete</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Launch Status & Messages - side by side */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Launch Status</Label>
                  <Select 
                    value={editForm.launch_status || "none"} 
                    onValueChange={(v) => setEditForm({...editForm, launch_status: v === "none" ? "" : v})}
                  >
                    <SelectTrigger data-testid="edit-launch-status">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not Set</SelectItem>
                      <SelectItem value="not_ready">Not Ready</SelectItem>
                      <SelectItem value="ready_to_launch">Ready to Launch</SelectItem>
                      <SelectItem value="launched">Launched</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Messages</Label>
                  <Select 
                    value={editForm.messages_status || "none"} 
                    onValueChange={(v) => setEditForm({...editForm, messages_status: v === "none" ? "" : v})}
                  >
                    <SelectTrigger data-testid="edit-messages-status">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not Set</SelectItem>
                      <SelectItem value="not_sent">Not Sent</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Follow Up Date</Label>
                  <Input
                    type="date"
                    value={editForm.follow_up_date}
                    onChange={(e) => setEditForm({...editForm, follow_up_date: e.target.value})}
                    data-testid="edit-follow-up-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Launch Date</Label>
                  <Input
                    type="date"
                    value={editForm.launch_date}
                    onChange={(e) => setEditForm({...editForm, launch_date: e.target.value})}
                    data-testid="edit-launch-date"
                  />
                </div>
              </div>

              {/* Comments */}
              <div className="space-y-2">
                <Label>Admin Comments</Label>
                <Textarea
                  value={editForm.admin_comments}
                  onChange={(e) => setEditForm({...editForm, admin_comments: e.target.value})}
                  placeholder="Add notes about this city..."
                  rows={3}
                  data-testid="edit-comments"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={saveCityDetails} disabled={saving} data-testid="save-city-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Radius Update Dialog */}
      <Dialog open={showRadiusDialog} onOpenChange={setShowRadiusDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Update City Radius</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-blue-50 rounded-lg text-sm text-blue-700">
              <p className="font-medium mb-1">What does city radius affect?</p>
              <p>City radius is used only for <strong>administrative purposes</strong> - determining which city an order belongs to for reporting.</p>
              <p className="mt-2">It does <strong>NOT</strong> affect rider matching. Riders are matched based on distance from pickup location.</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="bulk-radius">New Radius (km) for ALL Cities</Label>
              <Input
                id="bulk-radius"
                type="number"
                min="1"
                max="500"
                value={bulkRadius}
                onChange={(e) => setBulkRadius(e.target.value)}
                placeholder="e.g., 20"
                data-testid="bulk-radius-input"
              />
              <p className="text-xs text-muted-foreground">
                This will update all {total} cities to have a {bulkRadius}km service radius.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRadiusDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkRadiusUpdate} 
              disabled={updatingRadius}
              data-testid="confirm-bulk-radius-btn"
            >
              {updatingRadius ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Update All Cities
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CityManagementCRM;
